import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { DropdownMenuRoot, DropdownMenuTrigger, DropdownMenuPortal, DropdownMenuContent, } from 'reka-ui';
import EnNavigationArea from '../EnNavigationArea';
import EnDashboardIconWrapper from 'Components/icons/EnDashboardIconWrapper.vue';
import EnPowerAccountSwitch from '../../navigation/EnPowerAccountSwitch';
import EnImitateUserDialog from 'Components/modals/EnImitateUserDialog/EnImitateUserDialog.vue';
import EnTooltip from 'Components/base/EnTooltip/EnTooltip.vue';
import EnIcon from 'Components/icons/EnIcon.vue';
import { PowerSwitcherGroups } from 'Components/navigation/utils/navigationGroups.js';
import { NavigationIcons } from 'Components/icons/navigation/enIcons';
import { EnDashboardIconVariants } from 'Components/icons/EnDashboardIconVariants';
import { useAccountNavigation } from '../../navigation/utils/useAccountNavigation.ts';
import { decodeHtmlEntities } from 'Js/utils/entityUtils.js';
import { useStateStore } from 'StoresSpa/state';
import useNavigationData from '../../navigation/utils/useNavigationData';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnNavigationDrawer',
    props: /*@__PURE__*/ _mergeModels({
        showUserImitationMenuItem: { type: Boolean, required: false },
        isProdViewEnabled: { type: Boolean, required: false },
        isAuthUrl: { type: Boolean, required: false },
        isLoggedIn: { type: Boolean, required: false }
    }, {
        "rail": { type: Boolean },
        "railModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["update:rail", "toggle-prod-view"], ["update:rail"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        __expose();
        const { t } = useI18n();
        const router = useRouter();
        const isDashboardActive = ref(false);
        router.afterEach((to) => {
            // ToDo(Pta) Once components are moved, use ModuleNames.
            isDashboardActive.value = to.fullPath === '/';
        });
        const props = __props;
        const emit = __emit;
        const stateStore = useStateStore();
        const rail = _useModel(__props, 'rail');
        const accountMenuOpen = ref(false);
        const imitateUserDialogOpen = ref(false);
        const internalCurrentAccount = ref(null);
        const internalHasAccount = ref(false);
        const allEnPowerAccounts = computed(() => {
            const fromStore = stateStore.moduleState.EnPowerAccounts;
            if (Array.isArray(fromStore))
                return fromStore;
            // The config API only attaches EnPowerAccounts for certain modules, so on
            // other pages fall back to the server-rendered global used by the shell.
            const enPowerAccounts = stateStore.globalState?.EnPowerAccountsForUser;
            return Array.isArray(enPowerAccounts) ? enPowerAccounts : [];
        });
        const isDrawerTogglerHovered = ref(false);
        const isDevelopment = computed(() => stateStore?.globalState?.IsDev);
        const loaded = ref(false);
        let { buildUrl, userProfileUrl, reloadPage } = useAccountNavigation(stateStore.globalState?.AuthUrl ?? window.authUrl ?? '', stateStore.globalState?.EngineUrl ?? window.engineUrl ?? '');
        const { tryNavigateTo: navigateToModule } = useNavigationData({
            buildUrl: (url, account) => buildUrl(url, account),
            currentEnpowerAccount: internalCurrentAccount.value,
        }, emit, t);
        const userDisplayName = computed(() => decodeHtmlEntities(stateStore.globalState?.User?.DisplayName ?? stateStore.globalState?.User?.Email ?? ''));
        const authUrl = computed(() => stateStore.globalState?.AuthUrl ?? window.authUrl ?? '');
        const engineUrl = computed(() => stateStore.globalState?.EngineUrl ?? window.engineUrl ?? '/');
        const logoutUrl = computed(() => props.isAuthUrl && authUrl.value ? `${authUrl.value}/Account/Profile/Logout` : '/Account/Logout');
        watch(() => stateStore.globalState, (val) => {
            if (loaded.value)
                return;
            if (val?.AuthUrl) {
                loaded.value = true;
                ({ buildUrl, userProfileUrl, reloadPage } = useAccountNavigation(val.AuthUrl, val.EngineUrl ?? ''));
            }
        }, { deep: true });
        onMounted(() => {
            try {
                // EN.Auth pages never call stateStore.loadConfig, so globalState stays null there.
                // Fall back to the server-rendered globals, otherwise the drawer would never render on logout page.
                if ((stateStore.globalState?.AuthUrl && stateStore.globalState?.EngineUrl) || window.isAuthUrl) {
                    loaded.value = true;
                }
                const stored = localStorage.getItem('enPowerAccountId');
                const hasStored = stored && stored !== 'null' && stored !== 'undefined';
                if (hasStored && Array.isArray(allEnPowerAccounts.value)
                    && allEnPowerAccounts.value.find((a) => a?.Id === stored)) {
                    internalCurrentAccount.value = stored;
                }
                else if (!internalCurrentAccount.value && Array.isArray(allEnPowerAccounts.value) && allEnPowerAccounts.value.length) {
                    internalCurrentAccount.value = allEnPowerAccounts.value[0]?.Id ?? null;
                }
            }
            catch (error) {
                console.error('Error loading EnPower account from localStorage:', error);
            }
        });
        function tryNavigateTo(url) {
            accountMenuOpen.value = false;
            navigateToModule(url);
        }
        function updateRail(val) {
            try {
                emit('update:rail', val);
            }
            catch (e) {
                console.error('Error updating rail state:', e);
            }
        }
        function groupDisplayName(group) {
            return t(`navigation.${group?.PermissionName}`) || group?.Area || '';
        }
        function matchesPowerSwitcherGroup(group) {
            return Array.isArray(PowerSwitcherGroups) && PowerSwitcherGroups.includes(groupDisplayName(group));
        }
        function toggleProdView() {
            emit('toggle-prod-view');
            accountMenuOpen.value = false;
        }
        function onImitateUserClick() {
            imitateUserDialogOpen.value = true;
            accountMenuOpen.value = false;
        }
        const __returned__ = { t, router, isDashboardActive, props, emit, stateStore, rail, accountMenuOpen, imitateUserDialogOpen, internalCurrentAccount, internalHasAccount, allEnPowerAccounts, isDrawerTogglerHovered, isDevelopment, loaded, get buildUrl() { return buildUrl; }, set buildUrl(v) { buildUrl = v; }, get userProfileUrl() { return userProfileUrl; }, set userProfileUrl(v) { userProfileUrl = v; }, get reloadPage() { return reloadPage; }, set reloadPage(v) { reloadPage = v; }, navigateToModule, userDisplayName, authUrl, engineUrl, logoutUrl, tryNavigateTo, updateRail, groupDisplayName, matchesPowerSwitcherGroup, toggleProdView, onImitateUserClick, get DropdownMenuRoot() { return DropdownMenuRoot; }, get DropdownMenuTrigger() { return DropdownMenuTrigger; }, get DropdownMenuPortal() { return DropdownMenuPortal; }, get DropdownMenuContent() { return DropdownMenuContent; }, get EnNavigationArea() { return EnNavigationArea; }, EnDashboardIconWrapper, get EnPowerAccountSwitch() { return EnPowerAccountSwitch; }, EnImitateUserDialog, EnTooltip, EnIcon, get NavigationIcons() { return NavigationIcons; }, get EnDashboardIconVariants() { return EnDashboardIconVariants; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
